import './FeedBattle.sass';

import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTimer } from 'react-timer-hook';

import { FeedBattleProps } from './FeedBattle.interface';
import { ActionSheet, Block, Button } from '$uikit';
import { useTranslator } from '$hooks';
import { IcMore24 } from '$assets';
import { feedActions } from '$store/feed';
import { goTo } from '$navigation/helper';
import { BATTLE } from '$shared/constants/pages';
import { formatCurrency } from '$utils';
import { CurrencyEnum } from '$store/models';
import { Api } from '$api';

export const FeedBattle: FC<FeedBattleProps> = ({ battle: battlePassed }) => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const [battle, setBattle] = useState(battlePassed);
  const timer = useTimer({
    autoStart: true,
    expiryTimestamp: new Date(battle.endAt * 1000),
  });
  const [isMoreShown, setMoreShown] = useState(false);
  const reloadTimer = useRef<any>(0);

  const refreshBattle = useCallback(() => {
    if (reloadTimer.current === -1) {
      return;
    }

    clearTimeout(reloadTimer.current);
    Api.get('/feed/battle')
      .then((res) => {
        if (reloadTimer.current === -1) {
          return;
        }

        const data: any = res;
        if (data.battlePromoted) {
          setBattle(data.battlePromoted);
          reloadTimer.current = setTimeout(() => refreshBattle(), 5000);
        }
      })
      .catch(() => {
        if (reloadTimer.current === -1) {
          return;
        }

        reloadTimer.current = setTimeout(() => refreshBattle(), 5000);
      });
  }, [reloadTimer]);

  useEffect(() => {
    reloadTimer.current = setTimeout(() => refreshBattle(), 5000);
    return () => {
      clearTimeout(reloadTimer.current);
      reloadTimer.current = -1;
    };
  }, []);

  const percent = useMemo(() => {
    const total = battle.oneAmount + battle.twoAmount;
    if (total <= 0) {
      return {
        onePercent: 50,
        twoPercent: 50,
      };
    }

    const onePercent = Math.round((battle.oneAmount / total) * 100);
    const twoPercent = Math.round((battle.twoAmount / total) * 100);

    return {
      onePercent: parseInt(onePercent + '', 10),
      twoPercent: parseInt(twoPercent + '', 10),
    };
  }, [battle]);

  const handleMore = useCallback(() => {
    setMoreShown(true);
  }, []);

  const handleMoreChange = useCallback(
    (value) => {
      setMoreShown(false);

      if (value === 'hide') {
        dispatch(feedActions.hideNotification(`battle_promoted_${battle.id}`));
        dispatch(feedActions.setPromotedBattle(null));
      }
    },
    [battle],
  );

  const handlePress = useCallback(
    (e) => {
      if (e.target.closest('.FeedBattle__more')) {
        return;
      }

      goTo(BATTLE, { id: battle.id });
    },
    [battle],
  );

  const totalAmount = battle.oneAmount + battle.twoAmount;

  if (!timer.isRunning) {
    return null;
  }

  return (
    <Block className="FeedBattle" onClick={handlePress}>
      <ActionSheet
        isOpened={isMoreShown}
        options={[
          {
            label: t('battle_feed_hide'),
            value: 'hide',
          },
        ]}
        onChange={handleMoreChange}
      />
      <div className="FeedBattle__more" onClick={handleMore}>
        <IcMore24 />
      </div>
      <div className="FeedBattle__title">{battle.description}</div>
      {totalAmount > 0 && (
        <div className="FeedBattle__prize">
          {formatCurrency(totalAmount, CurrencyEnum.EUR)}
        </div>
      )}
      <div className="FeedBattle__timer__items">
        <div className="FeedBattle__timer__item">
          {(timer.days < 10 ? '0' : '') + timer.days}
          {t('battle_timer_days')}
        </div>
        <div className="FeedBattle__timer__item">
          {(timer.hours < 10 ? '0' : '') + timer.hours}
          {t('battle_timer_hours')}
        </div>
        <div className="FeedBattle__timer__item">
          {(timer.minutes < 10 ? '0' : '') + timer.minutes}
          {t('battle_timer_minutes')}
        </div>
        <div className="FeedBattle__timer__item">
          {(timer.seconds < 10 ? '0' : '') + timer.seconds}
          {t('battle_timer_seconds')}
        </div>
      </div>
      <div className="FeedBattle_photos">
        <div className="FeedBattle_photos__icon" />
        <div className="FeedBattle_photos__photo_wrap one">
          <div
            className="FeedBattle_photos__photo"
            style={{
              backgroundImage: `url(${battle.onePhoto})`,
            }}
          />
        </div>
        <div className="FeedBattle_photos__photo_wrap two">
          <div
            className="FeedBattle_photos__photo"
            style={{
              backgroundImage: `url(${battle.twoPhoto})`,
            }}
          />
        </div>
      </div>
      <div className="FeedBattle__progress">
        <div className="FeedBattle__pattern" />
        <div className="FeedBattle__progress__items">
          <div
            className="FeedBattle__progress__item"
            style={{ flex: percent.onePercent / 100 }}
          />
          <div
            className="FeedBattle__progress__item"
            style={{ flex: percent.twoPercent / 100 }}
          />
        </div>
        <div className="FeedBattle__progress__percents">
          <div className="FeedBattle__progress__percent">{percent.onePercent}%</div>
          <div className="FeedBattle__progress__percent">{percent.twoPercent}%</div>
        </div>
      </div>
      <div className="FeedBattle__actions">
        <Button>{t('battle_feed_open_btn')}</Button>
      </div>
    </Block>
  );
};
